/** @format */

import { createContext, useEffect, useState, type ReactNode } from 'react';
import _ from 'lodash';
import { THEMES } from 'src/constants';

export interface AppSettings {
  direction: 'ltr' | 'rtl';
  responsiveFontSizes: boolean;
  theme: string;
}

const defaultSettings: AppSettings = {
  direction: 'ltr',
  responsiveFontSizes: true,
  theme: THEMES.AUMOVIO_AFM
};

export interface SettingsContextValue {
  settings: AppSettings;
  saveSettings: (update?: Partial<AppSettings>) => void;
}

export const restoreSettings = (): AppSettings | null => {
  let settings: AppSettings | null = null;

  try {
    const storedData = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData) as AppSettings;
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a stringified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const storeSettings = (settings: AppSettings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  saveSettings: () => {}
});

export interface SettingsProviderProps {
  settings?: AppSettings;
  children?: ReactNode;
}

export const SettingsProvider = ({ settings, children }: SettingsProviderProps) => {
  const [currentSettings, setCurrentSettings] = useState<AppSettings>(settings || defaultSettings);

  const handleSaveSettings = (update: Partial<AppSettings> = {}) => {
    const mergedSettings = _.merge({}, currentSettings, update) as AppSettings;

    setCurrentSettings(mergedSettings);
    storeSettings(mergedSettings);
  };

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setCurrentSettings(restoredSettings);
    }
  }, []);

  useEffect(() => {
    document.dir = currentSettings.direction;
  }, [currentSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings: currentSettings,
        saveSettings: handleSaveSettings
      }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
